import { get, ref } from 'firebase/database';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { firestore, realtimeDb } from '../firebaseConfig';
import {
  TRANSACTION_TYPES,
  buildFirestoreTransaction,
} from '../domain/finance';
import {
  ACCOUNT_ROLES,
  consumeAccountInvite,
  ensureUserDocument,
  getMembership,
  selectCurrentAccount,
} from './accountService';

export const LEGACY_MIGRATION_VERSION = 1;
const MIGRATION_BATCH_SIZE = 350;

export const BOOTSTRAP_STAGES = Object.freeze({
  READ_USER: 'read-user-profile',
  READ_MEMBERSHIP: 'read-membership',
  READ_ACCOUNT: 'read-account',
  READ_LEGACY: 'read-legacy-rtdb',
  CREATE_FOUNDATION: 'create-foundation-atomic',
  RECOVER_FOUNDATION: 'recover-existing-foundation',
  MIGRATE_TRANSACTIONS: 'migrate-transactions',
  FINALIZE_MIGRATION: 'finalize-migration',
  SELECT_ACCOUNT: 'select-current-account',
});

export class AccountBootstrapError extends Error {
  constructor(stage, cause) {
    super(`ACCOUNT_BOOTSTRAP_FAILED:${stage}`);
    this.name = 'AccountBootstrapError';
    this.stage = stage;
    this.code = cause?.code || 'account/bootstrap-failed';
    this.cause = cause;
  }
}

const atStage = async (stage, operation) => {
  try {
    return await operation();
  } catch (error) {
    if (error instanceof AccountBootstrapError) throw error;
    throw new AccountBootstrapError(stage, error);
  }
};

const getLegacyAccountId = (uid) => `legacy_${uid}`;

const getLegacyData = async (uid, database = realtimeDb) => {
  const snapshot = await get(ref(database, `users/${uid}`));
  return snapshot.exists() ? snapshot.val() : {};
};

const getRuntime = (runtime = {}) => {
  const firestoreDb = runtime.firestoreDb || firestore;
  const realtimeDatabase = runtime.realtimeDatabase || realtimeDb;
  return {
    firestoreDb,
    loadLegacyData: runtime.loadLegacyData || ((uid) => getLegacyData(uid, realtimeDatabase)),
    consumeInvite: runtime.consumeInvite || ((args) => consumeAccountInvite({ ...args, firestoreDb })),
  };
};

const getStableTransactionId = (legacyId, type, collisions) => (
  collisions.has(legacyId)
    ? `${type === TRANSACTION_TYPES.INCOME ? 'income' : 'expense'}_${legacyId}`
    : legacyId
);

const buildUserFoundation = ({ user, userSnapshot, accountId, legacyData }) => ({
  uid: user.uid,
  email: user.email || '',
  displayName: user.displayName || '',
  photoURL: user.photoURL || '',
  currentAccountId: accountId,
  migrationVersion: userSnapshot.data()?.migrationVersion || 0,
  migrationStatus: 'in_progress',
  legacySharedAccessDetected: Boolean(legacyData.sharedAccess),
  updatedAt: serverTimestamp(),
  ...(userSnapshot.exists() ? {} : { createdAt: serverTimestamp() }),
});

const assertPersonalFoundation = (accountSnapshot, membership, user, accountId) => {
  if (!accountSnapshot.exists()) throw new Error('PERSONAL_ACCOUNT_NOT_FOUND');
  if (accountSnapshot.data().ownerUid !== user.uid) throw new Error('PERSONAL_ACCOUNT_OWNER_MISMATCH');
  if (membership?.role !== ACCOUNT_ROLES.OWNER || membership.accountId !== accountId) {
    throw new Error('PERSONAL_MEMBERSHIP_INVALID');
  }
};

const createMigrationFoundation = async (user, accountId, legacyData, firestoreDb) => {
  const userRef = doc(firestoreDb, 'users', user.uid);
  const accountRef = doc(firestoreDb, 'accounts', accountId);
  const memberRef = doc(firestoreDb, 'accounts', accountId, 'members', user.uid);
  const [initialUserSnapshot, membership] = await Promise.all([
    atStage(BOOTSTRAP_STAGES.READ_USER, () => getDoc(userRef)),
    atStage(BOOTSTRAP_STAGES.READ_MEMBERSHIP, () => getMembership(accountId, user.uid, firestoreDb)),
  ]);
  let userSnapshot = initialUserSnapshot;
  const requiredUserFields = ['uid', 'email', 'displayName', 'photoURL', 'createdAt', 'updatedAt'];
  const userData = userSnapshot.data() || {};
  const needsLegacyRepair = userSnapshot.exists()
    && requiredUserFields.some((field) => !Object.prototype.hasOwnProperty.call(userData, field));
  if (needsLegacyRepair) {
    await atStage(
      BOOTSTRAP_STAGES.RECOVER_FOUNDATION,
      () => ensureUserDocument(user, firestoreDb)
    );
    userSnapshot = await atStage(BOOTSTRAP_STAGES.READ_USER, () => getDoc(userRef));
  }
  const userPayload = buildUserFoundation({ user, userSnapshot, accountId, legacyData });

  if (membership) {
    const accountSnapshot = await atStage(BOOTSTRAP_STAGES.READ_ACCOUNT, () => getDoc(accountRef));
    assertPersonalFoundation(accountSnapshot, membership, user, accountId);
    await atStage(BOOTSTRAP_STAGES.CREATE_FOUNDATION, () => setDoc(userRef, userPayload, { merge: true }));
    return;
  }

  const foundationBatch = writeBatch(firestoreDb);
  foundationBatch.set(accountRef, {
    ownerUid: user.uid,
    name: 'Minha conta Nara',
    type: 'personal',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    schemaVersion: 1,
  });
  foundationBatch.set(memberRef, {
    uid: user.uid,
    accountId,
    role: ACCOUNT_ROLES.OWNER,
    relation: 'other',
    membershipType: 'permanent',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  foundationBatch.set(userRef, userPayload, { merge: true });

  try {
    await foundationBatch.commit();
    return;
  } catch (error) {
    if (error?.code !== 'permission-denied') {
      throw new AccountBootstrapError(BOOTSTRAP_STAGES.CREATE_FOUNDATION, error);
    }
  }

  // Recupera corrida entre abas ou conta determinística legada já existente.
  // A rule de membership só permite este write quando ownerUid da conta é o UID autenticado.
  await atStage(BOOTSTRAP_STAGES.RECOVER_FOUNDATION, async () => {
    const recoveredMembership = await getMembership(accountId, user.uid, firestoreDb);
    if (recoveredMembership) {
      const accountSnapshot = await getDoc(accountRef);
      assertPersonalFoundation(accountSnapshot, recoveredMembership, user, accountId);
      await setDoc(userRef, userPayload, { merge: true });
      return;
    }

    const recoveryBatch = writeBatch(firestoreDb);
    recoveryBatch.set(memberRef, {
      uid: user.uid,
      accountId,
      role: ACCOUNT_ROLES.OWNER,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    recoveryBatch.set(userRef, userPayload, { merge: true });
    await recoveryBatch.commit();
  });
};

const migrateTransactions = async (accountId, legacyData, firestoreDb) => {
  const incomeEntries = Object.entries(legacyData.receitas || {});
  const expenseEntries = Object.entries(legacyData.despesas || {});
  const incomeIds = new Set(incomeEntries.map(([id]) => id));
  const collisions = new Set(expenseEntries.map(([id]) => id).filter((id) => incomeIds.has(id)));
  const existingSnapshot = await getDocs(collection(firestoreDb, 'accounts', accountId, 'transactions'));
  const existingIds = new Set(existingSnapshot.docs.map((item) => item.id));
  const candidates = [
    ...incomeEntries.map(([id, record]) => ({ id, record, type: TRANSACTION_TYPES.INCOME })),
    ...expenseEntries.map(([id, record]) => ({ id, record, type: TRANSACTION_TYPES.EXPENSE })),
  ];

  let migrated = 0;
  let skippedExisting = 0;
  let invalid = 0;
  let batch = writeBatch(firestoreDb);
  let writesInBatch = 0;

  for (const candidate of candidates) {
    const id = getStableTransactionId(candidate.id, candidate.type, collisions);
    if (existingIds.has(id)) {
      skippedExisting += 1;
      continue;
    }

    const payload = buildFirestoreTransaction(candidate.record, candidate.type);
    if (!payload) {
      invalid += 1;
      continue;
    }

    batch.set(doc(firestoreDb, 'accounts', accountId, 'transactions', id), {
      ...payload,
      origin: 'legacyMigration',
      migrationVersion: LEGACY_MIGRATION_VERSION,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    writesInBatch += 1;
    migrated += 1;

    if (writesInBatch >= MIGRATION_BATCH_SIZE) {
      await batch.commit();
      batch = writeBatch(firestoreDb);
      writesInBatch = 0;
    }
  }

  if (writesInBatch > 0) await batch.commit();
  return { migrated, skippedExisting, invalid, total: candidates.length };
};

export const migrateLegacyUser = async (user, runtimeOptions = {}) => {
  const runtime = getRuntime(runtimeOptions);
  const { firestoreDb } = runtime;
  const userRef = doc(firestoreDb, 'users', user.uid);
  const currentUserSnapshot = await atStage(BOOTSTRAP_STAGES.READ_USER, () => getDoc(userRef));
  const currentData = currentUserSnapshot.data();

  if (currentData?.migrationVersion >= LEGACY_MIGRATION_VERSION && currentData.currentAccountId) {
    const membership = await atStage(
      BOOTSTRAP_STAGES.READ_MEMBERSHIP,
      () => getMembership(currentData.currentAccountId, user.uid, firestoreDb)
    );
    if (membership) {
      return { accountId: currentData.currentAccountId, role: membership.role, alreadyMigrated: true };
    }
  }

  const accountId = getLegacyAccountId(user.uid);
  const legacyData = await atStage(BOOTSTRAP_STAGES.READ_LEGACY, () => runtime.loadLegacyData(user.uid));
  await createMigrationFoundation(user, accountId, legacyData, firestoreDb);
  const stats = await atStage(
    BOOTSTRAP_STAGES.MIGRATE_TRANSACTIONS,
    () => migrateTransactions(accountId, legacyData, firestoreDb)
  );

  await atStage(BOOTSTRAP_STAGES.FINALIZE_MIGRATION, () => setDoc(userRef, {
    currentAccountId: accountId,
    migrationVersion: LEGACY_MIGRATION_VERSION,
    migrationStatus: 'complete',
    migrationStats: stats,
    migratedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true }));

  return { accountId, role: ACCOUNT_ROLES.OWNER, alreadyMigrated: false, stats };
};

export const bootstrapAccountForUser = async (user, inviteToken = null, runtimeOptions = {}) => {
  const runtime = getRuntime(runtimeOptions);
  const { firestoreDb } = runtime;
  const userRef = doc(firestoreDb, 'users', user.uid);
  const userSnapshot = await atStage(BOOTSTRAP_STAGES.READ_USER, () => getDoc(userRef));
  const userData = userSnapshot.data() || {};
  const storedAccountId = userData.currentAccountId || null;
  const storedMembership = storedAccountId
    ? await atStage(
      BOOTSTRAP_STAGES.READ_MEMBERSHIP,
      () => getMembership(storedAccountId, user.uid, firestoreDb)
    )
    : null;

  // A conta pessoal é consolidada antes de qualquer convite ou migração financeira.
  let personalAccount = null;
  if ((userData.migrationVersion || 0) < LEGACY_MIGRATION_VERSION) {
    personalAccount = await migrateLegacyUser(user, runtimeOptions);
  }

  let inviteError = null;
  let acceptedInvite = null;
  if (inviteToken) {
    try {
      acceptedInvite = await runtime.consumeInvite({ token: inviteToken, user });
    } catch (error) {
      inviteError = error.message;
    }
  }

  if (storedAccountId && storedMembership) {
    if (personalAccount && personalAccount.accountId !== storedAccountId) {
      await atStage(
        BOOTSTRAP_STAGES.SELECT_ACCOUNT,
        () => selectCurrentAccount(user.uid, storedAccountId, firestoreDb)
      );
    }
    return {
      accountId: storedAccountId,
      role: storedMembership.role,
      inviteAccepted: false,
      inviteError,
    };
  }

  if (personalAccount) {
    return { ...personalAccount, inviteAccepted: Boolean(acceptedInvite), invitedAccountId: acceptedInvite?.accountId || null, inviteError };
  }

  // Um ponteiro inválido nunca concede acesso. A conta pessoal determinística é recuperada.
  const recovered = await migrateLegacyUser(user, runtimeOptions);
  return { ...recovered, inviteAccepted: Boolean(acceptedInvite), invitedAccountId: acceptedInvite?.accountId || null, inviteError };
};
